import { Component } from 'vue-property-decorator';
import { Block } from '@/helpers/components';
import { BaseBlock } from '@/blocks/base-block';
import VueRecaptcha from 'vue-recaptcha';
import { WmsUserRequestDTO } from '../../../model/WmsUserRequestDTO';
import { range } from 'lodash-es';

interface FormErrors {
	nameEmpty: boolean;
	emailEmpty: boolean;
	emailNotValid: boolean;
	passwordEmpty: boolean;
	passwordsDontMatch: boolean;
	gdprEmpty: boolean;
	recaptchaNotVerified: boolean;
	submitFailed: boolean;
	userAlreadyExists: boolean;
}

@Block() @Component({
	components: { 'vue-recaptcha': VueRecaptcha }
})
export class FavoriteProductsUserRegFormBlock extends BaseBlock {
	isEdit: boolean = false;	// false = registration form, true = user edit form

	userRequest: WmsUserRequestDTO = null;
	password2: string = null;
	news: boolean = false;
	gdpr: boolean = false;

	errors: FormErrors = null;

	success: boolean = false;
	gdprInformation: string = null;

	created() {
		this.reset();

		this.$root.$on("user-reg-form-called", () => {
			this.$modal.show("user-reg-form");
		});

		this.$root.$on("user-edit-form-called", () => {
			this.isEdit = true;
			this.$modal.show("user-reg-form");
		});
	}

	opened() {
		const trackModalOpen = (window as any).trackModalOpen;

		if (typeof trackModalOpen === "function") {
			trackModalOpen();
		}

		if (this.isEdit) {
			// load user info
			this.$userService.getUserInfo()
				.then(u => {
					this.userRequest = {
						...this.userRequest,
						...{ id: u.id, fullName: u.fullName, email: u.email, phone: u.phone }
					}
				})
		}
	}

	verifyCaptcha(response: string) {
		this.userRequest.recaptchaResponse = response;
	}

	async submit(e: Event) {
		e.preventDefault();

		if (this.isValid()) {
			try {
				if (!this.isEdit) {
					// user registration
					this.userRequest.newsSubscriptionMail = this.news ? this.createNewsSubscriptionMail() : "";
					this.userRequest.newsSubscriptionMailTo = this.news ? this.getText('email-recipient-address') : "";
					await this.$userService.postRegisterUser(this.userRequest);
				}
				else {
					// change user info
					await this.$userService.postUserInfo(this.userRequest);
				}
				this.success = true;
				this.$root.$emit("user-changed");
			} catch (e) {
				if (e.response.status == 409 /* Conflict */) {
					this.errors.userAlreadyExists = true;
				}
				else {
					this.errors.submitFailed = true;
				}
				(this.$refs.recaptcha as VueRecaptcha).reset();
				this.userRequest.recaptchaResponse = "";
			}
		}
	}

	isValid() {
		Object.keys(this.errors).forEach((k: keyof FormErrors) => this.errors[k] = false);

		if (!this.userRequest.fullName || !this.userRequest.fullName.trim()) {
			this.errors.nameEmpty = true;
		}

		if (!this.userRequest.email || !this.userRequest.email.trim()) {
			this.errors.emailEmpty = true;
		}
		else if (!this.isValidEmail(this.userRequest.email)) {
			this.errors.emailNotValid = true;
		}

		if (!this.isEdit && (!this.userRequest.password || !this.userRequest.password.trim())) {
			this.errors.passwordEmpty = true;
		}
		else if (this.userRequest.password != this.password2) {
			this.errors.passwordsDontMatch = true;
		}

		if (!this.isEdit && !this.gdpr) {
			this.errors.gdprEmpty = true;
		}

		if (!this.isEdit && !this.userRequest.recaptchaResponse) {
			this.errors.recaptchaNotVerified = true;
		}

		return Object.keys(this.errors).every((k: keyof FormErrors) => !this.errors[k]);
	}

	isValidEmail(email: string) {
		const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return re.test(email);
	}

	reset() {
		this.isEdit = false;
		this.userRequest = {
			id: 0,
			fullName: "",
			email: "",
			password: "",
			recaptchaResponse: "",
			phone: "",
			favorites: "",
			notificationMailSubject: this.getText("registration-notification-email-subject"),
			notificationMailText: this.getText("registration-notification-email"),
			newsSubscriptionMail: "",
			newsSubscriptionMailTo: ""
		};
		this.password2 = "";
		this.news = false;
		this.gdpr = false;

		this.errors = {
			nameEmpty: false,
			emailEmpty: false,
			emailNotValid: false,
			passwordEmpty: false,
			passwordsDontMatch: false,
			gdprEmpty: false,
			recaptchaNotVerified: false,
			submitFailed: false,
			userAlreadyExists: false
		};

		this.success = false;
	}

	createNewsSubscriptionMail(): string {
		// gtm dataLayer.push to track form sending - see function gtm() in WMS2 CKontaktniFormularPopup
		const leadId = this.randomString(12);
		(window as any).dataLayer = (window as any).dataLayer || [];
		(window as any).dataLayer.push({
			'event': 'leadFormSent',
			'leadFormSentData': this.gtmLeadData(leadId),
			'eventCallback': function () {
				(window as any).dataLayer.push({ 'leadFormSentData': undefined });
			}
		})

		// prepare mail contents
		const messageHeader = `
				leadId: ${leadId}<br /><br />
				${this.getText('email-intro-text')}
				- page = ${this.$route.fullPath}<br />
				- jv = ${this.$store.langId}<br /><br />
				${this.getText('form-input-name')}: ${this.htmlEncode(this.userRequest.fullName)}<br /><br />
				${this.getText('form-input-email')}: ${this.userRequest.email}<br /><br />
				${this.getText('form-input-phone')}: ${this.htmlEncode(this.userRequest.phone)}<br /><br />
			`;

		return `${messageHeader}
				${this.getText('email-footer-text')}`;	// no need for footer logic because the mail is only sent if both gdpr and news are true
	}

	randomString(length: number) {
		const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
		return range(length).map(() => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
	}

	gtmLeadData(leadId: string) {
		let data: any = {};

		data.leadId = leadId;

		let locationId = null;
		let price = null;

		if (this.$store.pageData.apartment) {
			locationId = this.$store.pageData.apartment.location.id;
			price = this.$store.pageData.apartment.totalPriceWithVAT;
		}
		else if (this.$store.pageData.location) {
			locationId = this.$store.pageData.location.id;
		}

		if (locationId) {
			data.locationId = locationId.toString();
		}
		if (price) {
			data.price = price;
		}

		return data;
	}

	htmlEncode(text: string) {
		const auxDiv = document.createElement("div");
		auxDiv.innerText = text;
		return auxDiv.innerHTML;
	}
}

export default FavoriteProductsUserRegFormBlock;
